import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "age_range", title: "What's your age range?", options: ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"] },
  { key: "gender", title: "How do you identify?", options: ["Female", "Male", "Non-binary", "Prefer not to say"] },
  { key: "activity_level", title: "How active are you, typically?", options: ["Sedentary", "Lightly active", "Moderately active", "Very active"] },
  { key: "diet", title: "Which best describes your diet?", options: ["Omnivore", "Pescatarian", "Vegetarian", "Vegan", "Keto / Low-carb"] },
  { key: "sleep_quality", title: "How would you rate your sleep?", options: ["Restful", "Average", "Restless", "Poor"] },
  { key: "stress_level", title: "Your typical stress level?", options: ["Low", "Moderate", "High", "Very high"] },
];

const SEVERITY_LABELS = ["Mild", "Light", "Moderate", "Strong", "Severe"];

export default function Questionnaire() {
  const nav = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [concerns, setConcerns] = useState([]);
  const [severities, setSeverities] = useState({});
  const [notes, setNotes] = useState("");
  const [concernsList, setConcernsList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/concerns").then((r) => setConcernsList(r.data)).catch(() => {});
  }, []);

  // Steps: 6 attribute steps + concerns + severity (only if concerns selected)
  const totalSteps = STEPS.length + 2;
  const progress = Math.round(((stepIdx + 1) / totalSteps) * 100);
  const onConcernsStep = stepIdx === STEPS.length;
  const onSeverityStep = stepIdx === STEPS.length + 1;

  const canAdvance = onSeverityStep
    ? concerns.every((c) => severities[c] != null)
    : onConcernsStep
    ? concerns.length > 0
    : Boolean(answers[STEPS[stepIdx].key]);

  const next = () => {
    if (!canAdvance) return;
    if (onConcernsStep) {
      // Seed severities at moderate (3) for any newly added concerns
      const seeded = { ...severities };
      concerns.forEach((c) => {
        if (seeded[c] == null) seeded[c] = 3;
      });
      setSeverities(seeded);
    }
    if (stepIdx < totalSteps - 1) setStepIdx(stepIdx + 1);
  };
  const back = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  const toggleConcern = (key) => {
    setConcerns((c) => (c.includes(key) ? c.filter((k) => k !== key) : [...c, key]));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...answers, concerns, severities, notes };
      const { data } = await api.post("/recommendations", payload);
      toast.success("Your plan is ready");
      nav(`/recommendations/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const step = STEPS[stepIdx];
  const labelFor = (key) => concernsList.find((c) => c.key === key)?.label || key;

  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="questionnaire-page">
      <div className="mb-10">
        <div className="flex justify-between items-center mb-3">
          <p className="label-eyebrow">Step {stepIdx + 1} of {totalSteps}</p>
          <p className="text-xs" style={{ color: "var(--text-2)" }}>{progress}%</p>
        </div>
        <div className="h-1 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${progress}%`, background: "var(--brand)" }}
            data-testid="progress-bar"
          />
        </div>
      </div>

      <div className="fade-up" key={stepIdx}>
        {!onConcernsStep && !onSeverityStep && (
          <>
            <h1 className="font-serif text-3xl sm:text-4xl mb-8 leading-tight">{step.title}</h1>
            <div className="grid sm:grid-cols-2 gap-3">
              {step.options.map((opt) => {
                const selected = answers[step.key] === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setAnswers((a) => ({ ...a, [step.key]: opt }))}
                    className={`selectable-card text-left flex items-center justify-between ${selected ? "selected" : ""}`}
                    data-testid={`option-${step.key}-${opt.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                  >
                    <span>{opt}</span>
                    {selected && <Check className="w-4 h-4" strokeWidth={2} style={{ color: "var(--brand)" }} />}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {onConcernsStep && (
          <>
            <h1 className="font-serif text-3xl sm:text-4xl mb-3 leading-tight">
              What would you like to focus on?
            </h1>
            <p className="mb-8" style={{ color: "var(--text-2)" }}>
              Select all that resonate. We&rsquo;ll prioritise supplements that overlap.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {concernsList.map((c) => {
                const selected = concerns.includes(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => toggleConcern(c.key)}
                    className={`selectable-card text-left flex items-center justify-between ${selected ? "selected" : ""}`}
                    data-testid={`concern-${c.key}`}
                  >
                    <span>{c.label}</span>
                    {selected && <Check className="w-4 h-4" strokeWidth={2} style={{ color: "var(--brand)" }} />}
                  </button>
                );
              })}
            </div>

            <div className="mt-8">
              <label className="label-eyebrow block mb-2">Anything else? (optional)</label>
              <textarea
                className="input-soft"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. I take blood thinners, I'm pregnant, I'm caffeine-sensitive…"
                data-testid="notes-input"
              />
            </div>
          </>
        )}

        {onSeverityStep && (
          <>
            <h1 className="font-serif text-3xl sm:text-4xl mb-3 leading-tight">
              How intense are these for you?
            </h1>
            <p className="mb-8" style={{ color: "var(--text-2)" }}>
              We&rsquo;ll weight supplements toward the concerns that matter most.
            </p>
            <div className="space-y-5">
              {concerns.map((c) => {
                const val = severities[c] ?? 3;
                return (
                  <div key={c} className="card-soft p-5" data-testid={`severity-card-${c}`}>
                    <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                      <p className="font-serif text-xl">{labelFor(c)}</p>
                      <span
                        className="text-xs px-3 py-1 rounded-full"
                        style={{ background: "var(--bg-2)", color: "var(--text)", border: "1px solid var(--border)" }}
                      >
                        {SEVERITY_LABELS[val - 1]} · {val}/5
                      </span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={5}
                      step={1}
                      value={val}
                      onChange={(e) => setSeverities({ ...severities, [c]: Number(e.target.value) })}
                      className="w-full accent-[#4A5D4E]"
                      data-testid={`severity-${c}`}
                    />
                    <div className="flex justify-between text-xs mt-1" style={{ color: "var(--text-2)" }}>
                      <span>Mild</span>
                      <span>Severe</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="mt-12 flex items-center justify-between">
        <button
          onClick={back}
          className="btn-ghost text-sm flex items-center gap-1.5"
          disabled={stepIdx === 0}
          data-testid="questionnaire-back"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </button>

        {!onSeverityStep ? (
          <button
            onClick={next}
            disabled={!canAdvance}
            className="btn-primary flex items-center gap-2"
            data-testid="questionnaire-next"
          >
            Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={!canAdvance || submitting}
            className="btn-primary flex items-center gap-2"
            data-testid="questionnaire-submit"
          >
            {submitting ? "Crafting your plan…" : (<><Sparkles className="w-4 h-4" strokeWidth={1.5} /> Build my plan</>)}
          </button>
        )}
      </div>
    </main>
  );
}
